import { router } from 'expo-router';
import Head from 'expo-router/head';
import { usePostHog } from 'posthog-js/react';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
// Import above-the-fold components immediately (critical for LCP)
import Header from '../components/landing/sections/Header.js';
import HeroSection from '../components/landing/sections/HeroSection.js';
// Lazy load below-the-fold sections for better initial load performance
const IntroductorySection = lazy(() => import('../components/landing/sections/IntroductorySection.js'));
const ResearchSection = lazy(() => import('../components/landing/sections/ResearchSection.js'));
const HowItWorksSection = lazy(() => import('../components/landing/sections/HowItWorksSection.js'));
// REDESIGN: GetMoreSection removed from new design
// const GetMoreSection = lazy(() => import('../components/landing/sections/GetMoreSection.js'));
const TestimonialsSection = lazy(() => import('../components/landing/sections/TestimonialsSection.js'));
const FAQSection = lazy(() => import('../components/landing/sections/FAQSection.js'));
const FinalCTASection = lazy(() => import('../components/landing/sections/FinalCTASection.js'));
const Footer = lazy(() => import('../components/landing/sections/Footer.js'));
import { landingPageContent, landingPageVariants } from '../constants/Content.js';
import { useAuth } from '../context/AuthContext';
import { useExperiment } from '../hooks/useExperiment.js';
import { trackConversion } from '../utils/gtmHelpers';

const EXPERIMENT_CTA_MAP = {
  deals: 'Find Deals for Me',
  'price-check': 'Score Cars for Me',
  'running-costs': 'Score Running Costs for Me',
};

// Hero card images for preload (default + deals/price-check variants)
const heroCardSmall = require('../../assets/images/landing/responsive/hero-card/small.webp');
const heroCardMedium = require('../../assets/images/landing/responsive/hero-card/medium.webp');
// Static hero images for preload (car-finder variant)
const heroImageSmall = require('../../assets/images/landing/responsive/hero/small.webp');
const heroImageMedium = require('../../assets/images/landing/responsive/hero/medium.webp');

// Helper to get URI from Metro asset
const getAssetUri = (asset) => {
  if (typeof asset === 'string') return asset;
  if (typeof asset === 'object' && asset.uri) return asset.uri;
  return String(asset);
};

/**
 * The main screen that assembles all sections of the landing page.
 * @param {object} props
 * @param {string} [props.variant] - Landing page variant key (e.g. 'car-finder', 'deals', 'price-check')
 */
const LandingScreen = ({ variant }) => {
  const [sectionLayouts, setSectionLayouts] = useState({});
  const posthog = usePostHog();
  const { user } = useAuth();

  // Resolve variant overrides
  const variantOverrides = variant ? landingPageVariants[variant] : null;
  const variantLabel = variantOverrides ? variant : 'default';

  // CTA experiment: only eligible for deals and price-check variants
  const isExperimentEligible = variant === 'deals' || variant === 'price-check' || variant === 'running-costs';
  const { variant: experimentVariant } = useExperiment(
    isExperimentEligible ? 'landing-cta-variant-test' : ''
  );
  const resolvedCtaTitle =
    isExperimentEligible && experimentVariant.key === 'test'
      ? EXPERIMENT_CTA_MAP[variant]
      : landingPageContent.heroSection.cta.title;

  // Merge hero section: variant heroSection overrides default, but keeps cta/heroCards/featuresBar as fallback
  const heroContent = variantOverrides?.heroSection
    ? {
        ...landingPageContent.heroSection,
        ...variantOverrides.heroSection,
        featuresBar: variantOverrides.heroSection.featuresBar || landingPageContent.heroSection.featuresBar,
        cta: { title: resolvedCtaTitle },
      }
    : { ...landingPageContent.heroSection, cta: { title: resolvedCtaTitle } };

  // Merge SEO: variant seo overrides title/description only
  const seoContent = variantOverrides?.seo
    ? { ...landingPageContent.seo, ...variantOverrides.seo }
    : landingPageContent.seo;

  // Call all useRef hooks at top level (required for React Compiler)
  const aboutRef = useRef(null);
  const sourcesRef = useRef(null);
  const howItWorksRef = useRef(null);
  const testimonialsRef = useRef(null);
  const faqRef = useRef(null);
  const scrollDepthTracked = useRef({ 25: false, 50: false, 75: false, 100: false });
  const scrollY = useSharedValue(0);

  // Create refs object after all hooks are called
  const refs = {
    '#about': aboutRef,
    '#sources': sourcesRef,
    '#how-it-works': howItWorksRef,
    '#testimonials': testimonialsRef,
    '#faq': faqRef,
  };

  useEffect(() => {
    posthog.capture('landing_page_viewed', {
      timestamp: new Date().toISOString(),
      variant: variantLabel,
    });

    // Handle URL hash on page load for sitelinks
    if (Platform.OS === 'web') {
      const hash = window.location.hash;
      if (hash && refs[hash]) {
        // Delay to ensure layout is complete
        setTimeout(() => {
          refs[hash].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [posthog.capture]);

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      scrollY.set(event.contentOffset.y);

      // Track scroll depth milestones
      const scrollPercentage =
        (event.contentOffset.y / (event.contentSize.height - event.layoutMeasurement.height)) * 100;

      if (scrollPercentage >= 25 && scrollPercentage < 50 && !scrollDepthTracked.current['25']) {
        posthog.capture('landing_scroll_depth', { depth: '25%' });
        scrollDepthTracked.current['25'] = true;
      } else if (
        scrollPercentage >= 50 &&
        scrollPercentage < 75 &&
        !scrollDepthTracked.current['50']
      ) {
        posthog.capture('landing_scroll_depth', { depth: '50%' });
        scrollDepthTracked.current['50'] = true;
      } else if (
        scrollPercentage >= 75 &&
        scrollPercentage < 100 &&
        !scrollDepthTracked.current['75']
      ) {
        posthog.capture('landing_scroll_depth', { depth: '75%' });
        scrollDepthTracked.current['75'] = true;
      } else if (scrollPercentage >= 100 && !scrollDepthTracked.current['100']) {
        posthog.capture('landing_scroll_depth', { depth: '100%' });
        scrollDepthTracked.current['100'] = true;
      }
    },
  });

  const handleCtaPress = (location = 'hero') => {
    // Track in PostHog
    posthog.capture('landing_cta_clicked', {
      cta_location: location,
      variant: variantLabel,
      ...(isExperimentEligible && {
        experiment_key: 'landing-cta-variant-test',
        variant_key: experimentVariant.key,
        cta_text: resolvedCtaTitle,
      }),
    });

    // Track in GTM - always send enhanced_conversion_data (null if no email)
    trackConversion(
      'landing_cta_clicked',
      {
        user_id: user?.id,
        cta_location: location,
        user_authenticated: !!user?.email,
      },
      user?.email ? { email: user.email } : null
    );

    // Navigate to the onboarding flow with intent from variant config
    const intent = variantOverrides?.onboardingIntent || null;
    const onboardingUrl = intent ? `/onboarding?intent=${intent}` : '/onboarding';
    router.push(onboardingUrl);
  };

  const handleNavPress = (target) => {
    if (target.startsWith('/')) {
      posthog.capture('landing_nav_link_clicked', {
        target_path: target,
      });
      router.push(target);
    } else {
      posthog.capture('landing_section_navigation', {
        target_section: target,
      });
      if (Platform.OS === 'web') {
        const ref = refs[target];
        if (ref?.current) {
          ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        const layout = sectionLayouts[target];
        if (layout && scrollViewRef.current) {
          scrollViewRef.current.scrollTo({ y: layout.y, animated: true });
        }
      }
    }
  };

  // Helper to safely update section layouts (deferred to avoid React #419 error)
  const handleSectionLayout = (section, nativeEvent) => {
    queueMicrotask(() => {
      setSectionLayouts((prev) => ({ ...prev, [section]: nativeEvent.layout }));
    });
  };

  const scrollViewRef = useRef(null);

  return (
    <View style={styles.container}>
      <Head>
        {/* Primary SEO Tags */}
        <title>{seoContent.title}</title>
        <meta name="description" content={seoContent.description} />
        <link rel="canonical" href="https://www.doocars.com/" />
        <link rel="alternate" hrefLang="en-gb" href="https://www.doocars.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://www.doocars.com/" />
        {/* LCP Image Preload - Responsive (variant-aware) */}
        {heroContent.heroImage ? (
          <>
            <link
              rel="preload"
              as="image"
              href={getAssetUri(heroImageSmall)}
              media="(max-width: 767px)"
              fetchPriority="high"
            />
            <link
              rel="preload"
              as="image"
              href={getAssetUri(heroImageMedium)}
              media="(min-width: 768px)"
              fetchPriority="high"
            />
          </>
        ) : (
          <>
            <link
              rel="preload"
              as="image"
              href={getAssetUri(heroCardSmall)}
              media="(max-width: 767px)"
              fetchPriority="high"
            />
            <link
              rel="preload"
              as="image"
              href={getAssetUri(heroCardMedium)}
              media="(min-width: 768px)"
              fetchPriority="high"
            />
          </>
        )}
        {/* Open Graph Tags (for Facebook, LinkedIn, etc.) */}
        <meta property="og:title" content={landingPageContent.seo.og.title} />
        <meta property="og:description" content={landingPageContent.seo.og.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={landingPageContent.seo.og.url} />
        <meta property="og:image" content={landingPageContent.seo.og.image} />
        <meta property="og:site_name" content="Doocars" />
        <meta property="og:locale" content="en_GB" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content={landingPageContent.seo.twitter.card} />
        <meta name="twitter:title" content={landingPageContent.seo.twitter.title} />
        <meta name="twitter:description" content={landingPageContent.seo.twitter.description} />
        <meta name="twitter:image" content={landingPageContent.seo.twitter.image} />

        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Doocars',
            url: 'https://www.doocars.com/',
            logo: 'https://www.doocars.com/assets/images/logo-dark.svg',
          })}
        </script>

        {/* WebSite Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Doocars',
            url: 'https://www.doocars.com/',
          })}
        </script>

        {/* WebPage Schema for Sitelinks */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Doocars — Daily UK Used Car Market Scanner',
            url: 'https://www.doocars.com/',
            hasPart: [
              {
                '@type': 'WebPageElement',
                name: 'About',
                url: 'https://www.doocars.com/#about',
              },
              {
                '@type': 'WebPageElement',
                name: 'Our Sources',
                url: 'https://www.doocars.com/#sources',
              },
              {
                '@type': 'WebPageElement',
                name: 'How It Works',
                url: 'https://www.doocars.com/#how-it-works',
              },
              {
                '@type': 'WebPageElement',
                name: 'Testimonials',
                url: 'https://www.doocars.com/#testimonials',
              },
              {
                '@type': 'WebPageElement',
                name: 'FAQ',
                url: 'https://www.doocars.com/#faq',
              },
            ],
          })}
        </script>

        {/* FAQPage Schema — drives rich results in SERPs */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: landingPageContent.faqSection.questions.map((q) => ({
              '@type': 'Question',
              name: q.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: q.answer,
              },
            })),
          })}
        </script>
      </Head>
      <Header
        content={{ ...landingPageContent.header, cta: { title: resolvedCtaTitle } }}
        onCtaPress={() => handleCtaPress('header')}
        onNavPress={handleNavPress}
      />
      <Animated.ScrollView
        ref={scrollViewRef}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        style={styles.content}
      >
        <View
          ref={refs['#about']}
          {...(Platform.OS === 'web' && { id: 'about' })}
          onLayout={({ nativeEvent }) => handleSectionLayout('#about', nativeEvent)}
        >
          <HeroSection
            content={heroContent}
            onCtaPress={() => handleCtaPress('hero')}
          />
        </View>
        <Suspense fallback={<ActivityIndicator size="large" style={{ marginTop: 72 }} />}>
          <View
            ref={refs['#sources']}
            {...(Platform.OS === 'web' && { id: 'sources' })}
            onLayout={({ nativeEvent }) => handleSectionLayout('#sources', nativeEvent)}
          >
            <ResearchSection content={landingPageContent.researchSection} />
          </View>
          <IntroductorySection content={landingPageContent.introductorySection} />
          <View
            ref={refs['#how-it-works']}
            {...(Platform.OS === 'web' && { id: 'how-it-works' })}
            onLayout={({ nativeEvent }) => handleSectionLayout('#how-it-works', nativeEvent)}
          >
            <HowItWorksSection scrollY={scrollY} parentRef={refs['#how-it-works']} />
          </View>
          {/* REDESIGN: GetMoreSection removed from new design */}
          {/* <GetMoreSection content={landingPageContent.getMoreSection} /> */}
          <View
            ref={refs['#testimonials']}
            {...(Platform.OS === 'web' && { id: 'testimonials' })}
            onLayout={({ nativeEvent }) => handleSectionLayout('#testimonials', nativeEvent)}
          >
            <TestimonialsSection content={landingPageContent.testimonialsSection} />
          </View>
          <View
            ref={refs['#faq']}
            {...(Platform.OS === 'web' && { id: 'faq' })}
            onLayout={({ nativeEvent }) => handleSectionLayout('#faq', nativeEvent)}
          >
            <FAQSection content={landingPageContent.faqSection} />
          </View>
          <FinalCTASection
            content={{
              cta: { ...landingPageContent.finalCTA, buttonText: resolvedCtaTitle },
            }}
            onCtaPress={() => handleCtaPress('final_cta')}
          />
          <Footer content={landingPageContent.footer} onNavPress={handleNavPress} />
        </Suspense>
      </Animated.ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  content: {
    // alignItems: 'center', // This is now handled within each section
  },
});

export default LandingScreen;
